import {
  formatArea,
  formatDistance,
} from "@/components/common/settings-context/distance-unit-utils";
import { useDistanceUnit } from "@/components/common/settings-context/settings-context";
import {
  FaroText,
  grey,
  NO_TRANSLATE_CLASS,
} from "@faro-lotv/app-component-toolbox";
import {
  areaOfClosedPolygon,
  polygonPerimeter,
  polygonPlanarity,
  VectorArea,
} from "@faro-lotv/lotv";
import { Paper } from "@mui/material";
import { HtmlProps } from "@react-three/drei/web/Html";
import {
  MutableRefObject,
  RefObject,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
  type JSX,
} from "react";
import { Matrix4, Vector3 } from "three";
import {
  INACTIVE_MEASURES_OPACITY,
  MEASURE_ANIMATION_LENGTH,
} from "./measure-constants";
import { PositionedHtml } from "./positioned-html";

export type ShapeLabelActions = {
  /** A callback to force the update of the rendering elements of this component */
  updateRendering(): void;
};

type ShapeLabelProps = {
  /** The list of points describing the polyline */
  points: Vector3[];
  /** A flag specifying if the measurement is a closed polygon or not */
  isClosed: boolean;
  /** A ref to the parent HTML element to which all labels will be attached */
  parentRef: MutableRefObject<HTMLElement>;
  /** The HTML property for the pointer events on the HTML labels */
  pointerEvents: HtmlProps["pointerEvents"];
  /** A flag to make the label transparent */
  transparent: boolean;
  /** The world matrix of the whole measurement */
  worldMatrix: Matrix4;
  /** The actions that can be manually triggered from outside  */
  actions?: RefObject<ShapeLabelActions | null>;
  /**
   * Callback providing center position of the segment when a label is clicked
   *
   * @param position of the segment center
   */
  onClick?(position: Vector3): void;
  /** Flag indicating the value display is an approximation */
  isApproximation?: boolean;
};

/** Temporary data to avoid re-allocations */
const VECTOR_AREA: VectorArea = {
  area: 0,
  normal: new Vector3(),
};

/** @returns a label to be placed at the centre of a closed polygon */
export function ShapeLabel({
  isClosed,
  parentRef,
  points,
  worldMatrix,
  transparent,
  pointerEvents,
  actions,
  onClick,
  isApproximation,
}: ShapeLabelProps): JSX.Element | null {
  const [planarity, setPlanarity] = useState(false);
  const [area, setArea] = useState<number | undefined>();
  const [perimeter, setPerimeter] = useState<number | undefined>();
  const [mean] = useState(() => new Vector3());

  const distanceUnit = useDistanceUnit();
  const label = useMemo(() => {
    const prefix = isApproximation ? "~" : "";
    let text = "";
    if (area) {
      // Convert from meters
      text += planarity
        ? `\u2206: ${prefix}${formatArea(area, distanceUnit)};`
        : "not planar";
    }
    if (perimeter) {
      text += `\u2211: ${prefix}${formatDistance(perimeter, distanceUnit)}`;
    }
    return text;
  }, [area, distanceUnit, isApproximation, perimeter, planarity]);

  // For computing the area, we need to take into account the 3D pose of the object, because it could contain
  // scaling values
  const adjustedPositions = useMemo(
    () => points.map((p) => p.clone().applyMatrix4(worldMatrix)),
    [points, worldMatrix],
  );

  const updateRendering = useCallback(() => {
    if (isClosed) {
      // Computing the area of the closed polygon and the normal of the plane
      // on which it makes sense.
      const result = areaOfClosedPolygon(adjustedPositions, VECTOR_AREA).area;
      // Computing to what extent the polygon is really described by the found plane
      const planarity = polygonPlanarity(adjustedPositions, VECTOR_AREA.normal);
      setArea(result);
      setPlanarity(planarity < 0.1);
    } else {
      setArea(undefined);
    }

    if (adjustedPositions.length < 3) {
      setPerimeter(undefined);
    } else {
      const result = polygonPerimeter(adjustedPositions, isClosed);
      setPerimeter(result);
    }

    mean.set(0, 0, 0);
    if (points.length === 0) {
      return;
    }
    points.reduce((prev, next) => {
      prev.add(next);
      return prev;
    }, mean);
    mean.divideScalar(points.length);
  }, [isClosed, mean, points, adjustedPositions]);

  const [opacity, setOpacity] = useState(0);

  useEffect(() => {
    if (transparent) setOpacity(INACTIVE_MEASURES_OPACITY);
    else setOpacity(1);
  }, [transparent]);

  useEffect(() => {
    updateRendering();
  }, [updateRendering]);

  useImperativeHandle(actions, () => ({
    updateRendering,
  }));

  if (!area && !perimeter) {
    return null;
  }

  return (
    <PositionedHtml
      position={mean}
      portal={parentRef}
      style={{
        pointerEvents,
        display: "block",
      }}
      zIndexRange={[0, 0]}
    >
      {/* Main Body */}
      <Paper
        elevation={0}
        onClick={() => onClick?.(mean)}
        sx={{
          padding: "5px",
          userSelect: "text",
          cursor: "text",
          opacity,
          transition: `opacity ${MEASURE_ANIMATION_LENGTH}s linear`,
          transform: "translate(-50%, -50%)",
          pointerEvents,
          backgroundColor: grey[1000],
          outline: `${grey[0]}33 solid 1px`,
        }}
      >
        <FaroText
          variant="bodyS"
          component="div"
          noWrap
          sx={{
            fontSize: "0.75em",
            fontWeight: "inherit",
            // Relative, so the line keeps scaling with the em-based font size
            lineHeight: 1.5,
            color: grey[0],
          }}
          className={NO_TRANSLATE_CLASS}
        >
          {label}
        </FaroText>
      </Paper>
    </PositionedHtml>
  );
}
